import { defineStore } from "pinia";
import { reactive, shallowRef, triggerRef } from "vue";
import { getUserCard } from "@/entities/user/api";

const TTL_FRESH = 5 * 60_000;
const TTL_HARD = 30 * 60_000;
const MAX_ENTRIES = 500;

/**
 * Кеш UserCard с LRU + TTL + SWR + request dedup + cancellation.
 *
 * Архитектура:
 * - `entries`: shallowRef<Map<id, CacheEntry>>; каждая entry — reactive объект,
 *   подписка идёт только на нужную запись, остальные cards не ререндерятся.
 * - `lru`: список id от MRU к LRU; touch() ротирует, eviction обрезает хвост.
 * - Запросы дедуплицируются через `entry.promise`; повторный fetchUser
 *   на in-flight id возвращает существующий promise.
 * - AbortController у каждой entry — `cancel(id)` останавливает запрос
 *   (например, при close hover-card до прихода ответа).
 * - SWR: если данные есть, но stale, отдаём их немедленно и refetch в фоне.
 *
 * @typedef {Object} CacheEntry
 * @property {import('./types').UserCard | null} data
 * @property {'idle'|'loading'|'success'|'error'} status
 * @property {Error|null}            error
 * @property {number}                fetchedAt
 * @property {Promise<unknown>|null} promise
 * @property {AbortController|null}  controller
 */

export const useUserCardCacheStore = defineStore("user-card-cache", () => {
  const entriesRef = shallowRef(new Map());
  const lru = [];

  function get(id) {
    return entriesRef.value.get(id);
  }

  function setEntry(id, entry) {
    entriesRef.value.set(id, entry);
    triggerRef(entriesRef);
  }

  function touch(id) {
    const i = lru.indexOf(id);
    if (i > -1) lru.splice(i, 1);
    lru.unshift(id);
    while (lru.length > MAX_ENTRIES) {
      const evictId = lru.pop();
      if (evictId === id) continue;
      const ev = entriesRef.value.get(evictId);
      if (ev?.controller) ev.controller.abort();
      entriesRef.value.delete(evictId);
    }
    triggerRef(entriesRef);
  }

  function isFresh(entry) {
    return (
      entry?.status === "success" && Date.now() - entry.fetchedAt < TTL_FRESH
    );
  }

  function isHardStale(entry) {
    return !entry || Date.now() - entry.fetchedAt > TTL_HARD;
  }

  /**
   * @param {string} id
   * @param {((id: string, opts: { signal: AbortSignal }) => Promise<unknown>)} [fetcher]
   */
  function fetchUser(id, fetcher = getUserCard) {
    if (!id) return Promise.resolve(null);

    const existing = get(id);

    if (isFresh(existing)) {
      touch(id);
      return Promise.resolve(existing.data);
    }

    if (existing?.promise) {
      touch(id);
      return existing.promise;
    }

    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;

    const entry = reactive({
      data: !isHardStale(existing) ? existing?.data ?? null : null,
      status: existing?.data && !isHardStale(existing) ? "success" : "loading",
      error: null,
      fetchedAt: existing?.fetchedAt ?? 0,
      promise: null,
      controller,
    });

    const promise = Promise.resolve()
      .then(() => fetcher(id, { signal: controller?.signal }))
      .then((data) => {
        entry.data = data;
        entry.status = "success";
        entry.error = null;
        entry.fetchedAt = Date.now();
        return data;
      })
      .catch((err) => {
        const aborted =
          err?.name === "AbortError" ||
          err?.name === "CanceledError" ||
          err?.code === "ERR_CANCELED";
        if (!aborted) {
          entry.status = "error";
          entry.error = err;
        }
        throw err;
      })
      .finally(() => {
        entry.promise = null;
        entry.controller = null;
      });

    entry.promise = promise;
    setEntry(id, entry);
    touch(id);

    // Swallow unhandled rejection — callers решают, ловить ли явно.
    promise.catch(() => {});
    return promise;
  }

  function cancel(id) {
    const e = get(id);
    if (e?.controller) e.controller.abort();
  }

  function invalidate(id) {
    if (id) {
      const e = get(id);
      if (e?.controller) e.controller.abort();
      entriesRef.value.delete(id);
      const i = lru.indexOf(id);
      if (i > -1) lru.splice(i, 1);
      triggerRef(entriesRef);
    } else {
      for (const e of entriesRef.value.values()) {
        if (e.controller) e.controller.abort();
      }
      entriesRef.value.clear();
      lru.length = 0;
      triggerRef(entriesRef);
    }
  }

  return {
    entriesRef,
    fetchUser,
    cancel,
    invalidate,
    get,
  };
});
